// CollegeTopPerformers.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Medal, Award } from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';

// --- Static student data (unchanged) -----------------------------------------
const students = [
  { name: 'Eleanor Pena',   email: 'eleanor.pena@example.com',   branch: 'Computer Science',   technology: 'React',   level: 3  },
  { name: 'Jessia Rose',    email: 'jessia.rose@example.com',    branch: 'Information Tech',   technology: 'Node.js', level: 8  },
  { name: 'Guy Hawkins',    email: 'guy.hawkins@example.com',    branch: 'Electronics',        technology: 'Python',  level: 14 },
  { name: 'Jacob Jones',    email: 'jacob.jones@example.com',    branch: 'Computer Science',   technology: 'React',   level: 18 },
  { name: 'Alice Watson',   email: 'alice.watson@example.com',   branch: 'Computer Science',   technology: 'React',   level: 17 },
  { name: 'Mark Lewis',     email: 'mark.lewis@example.com',     branch: 'Information Tech',   technology: 'Node.js', level: 15 },
  { name: 'Emma Brooks',    email: 'emma.brooks@example.com',    branch: 'Electronics',        technology: 'Python',  level: 16 },
  { name: 'Ryan Hall',      email: 'ryan.hall@example.com',      branch: 'Computer Science',   technology: 'React',   level: 12 },
  { name: 'Sophia King',    email: 'sophia.king@example.com',    branch: 'Information Tech',   technology: 'Node.js', level: 13 },
  { name: 'Daniel Wright',  email: 'daniel.wright@example.com',  branch: 'Electronics',        technology: 'Python',  level: 11 },
  { name: 'Chris Young',    email: 'chris.young@example.com',    branch: 'Computer Science',   technology: 'React',   level: 9  },
];

const topStudents = [...students].sort((a, b) => b.level - a.level).slice(0, 10);

// --- Design tokens ------------------------------------------------------------
const T = {
  border: '1px solid var(--border-subtle, #E2E8F0)',
  radius: { sm: '8px', md: '10px', lg: '14px', xl: '18px', full: '999px' },
  shadow: { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)' },
};

// --- Rank medal config --------------------------------------------------------
const rankConfig = (rank) => {
  if (rank === 1) return { bg: '#FFF7ED', border: '#FDE68A', color: '#D97706', label: '🥇', strip: '#F59E0B' };
  if (rank === 2) return { bg: '#F8FAFC', border: '#CBD5E1', color: '#64748B', label: '🥈', strip: '#94A3B8' };
  if (rank === 3) return { bg: '#FFF7ED', border: '#FED7AA', color: '#EA580C', label: '🥉', strip: '#F97316' };
  return { bg: '#FFFFFF', border: '#E2E8F0', color: 'var(--primary-blue, #4F46E5)', label: `#${rank}`, strip: 'var(--primary-blue, #4F46E5)' };
};

const levelColor = (level) => {
  if (level >= 16) return { bg: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0' };
  if (level >= 11) return { bg: '#DBEAFE', color: 'var(--secondary-blue, #1E3A8A)', border: '1px solid #BFDBFE' };
  if (level >= 6)  return { bg: '#FEF9C3', color: '#CA8A04', border: '1px solid #FDE047' };
  return { bg: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' };
};

const techColor = (tech) => {
  if (tech === 'React')   return { bg: '#EFF6FF', color: 'var(--primary-blue, #4F46E5)', border: '1px solid #BFDBFE' };
  if (tech === 'Node.js') return { bg: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0' };
  if (tech === 'Python')  return { bg: '#F3E8FF', color: '#7C3AED', border: '1px solid #E9D5FF' };
  return { bg: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' };
};

// --- Top 3 podium cards -------------------------------------------------------
const PodiumCard = ({ student, rank }) => {
  const cfg  = rankConfig(rank);
  const lvl  = levelColor(student.level);
  const order = rank === 1 ? 0 : rank === 2 ? -1 : 1; // gold centre

  return (
    <div style={{
      background: '#FFFFFF', borderRadius: T.radius.xl, border: `1px solid ${cfg.border}`,
      boxShadow: rank === 1 ? '0 8px 30px rgba(245,158,11,0.15)' : T.shadow.sm,
      padding: '24px 20px', textAlign: 'center', flex: '1 1 200px',
      position: 'relative', overflow: 'hidden',
      transform: rank === 1 ? 'translateY(-8px)' : 'none',
      transition: 'transform 0.2s',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: cfg.strip, borderRadius: `${T.radius.xl} ${T.radius.xl} 0 0` }} />
      <div style={{ fontSize: '2rem', marginBottom: 12 }}>{cfg.label}</div>
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: `linear-gradient(135deg, ${cfg.strip}20, ${cfg.strip}40)`,
        border: `2px solid ${cfg.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 12px',
        fontSize: '1.25rem', fontWeight: 800, color: cfg.color,
      }}>
        {student.name[0]}
      </div>
      <p style={{ fontWeight: 800, color: '#0F172A', fontSize: '0.9375rem', margin: '0 0 4px' }}>{student.name}</p>
      <p style={{ fontSize: '0.75rem', color: '#64748B', margin: '0 0 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{student.branch}</p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
        <span style={{ ...lvl, padding: '3px 10px', borderRadius: T.radius.full, fontSize: '0.72rem', fontWeight: 700 }}>
          Level {student.level}
        </span>
      </div>
    </div>
  );
};

// --- Main component -----------------------------------------------------------
const CollegeTopPerformers = () => {
  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Trophy size={22} style={{ color: '#D97706' }} />
                </div>
                <div>
                  <h1 style={{ fontSize: '1.625rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', margin: 0 }}>Top Performers</h1>
                  <p style={{ color: '#64748B', fontSize: '0.875rem', margin: '3px 0 0' }}>Top 10 students ranked by skill level</p>
                </div>
              </div>
              <Link to="/TotalStudents" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: T.radius.md,
                background: '#FFFFFF', border: T.border, textDecoration: 'none',
                fontSize: '0.8125rem', fontWeight: 600, color: '#374151',
                boxShadow: T.shadow.xs,
              }}>
                ← Student Directory
              </Link>
            </div>

            {/* Podium - top 3 */}
            <div style={{
              display: 'flex', gap: 16, marginBottom: 24,
              padding: '24px 20px',
              background: 'linear-gradient(135deg, #FFF7ED 0%, #FFFBEB 50%, #FFF7ED 100%)',
              border: '1px solid #FDE68A', borderRadius: T.radius.xl,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #F59E0B, #D97706, #F59E0B)', borderRadius: `${T.radius.xl} ${T.radius.xl} 0 0` }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, flex: 1, alignItems: 'flex-end', justifyContent: 'center' }}>
                {/* Order: 2nd, 1st, 3rd for visual podium effect */}
                <PodiumCard student={topStudents[1]} rank={2} />
                <PodiumCard student={topStudents[0]} rank={1} />
                <PodiumCard student={topStudents[2]} rank={3} />
              </div>
            </div>

            {/* Full leaderboard table */}
            <div style={{
              background: '#FFFFFF', border: T.border, borderRadius: T.radius.lg,
              boxShadow: T.shadow.sm, overflow: 'hidden', position: 'relative',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #D97706, var(--primary-blue, #4F46E5), #7C3AED)', zIndex: 1 }} />
              <div style={{ overflowX: 'auto', marginTop: 4 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F8FAFC', borderBottom: T.border }}>
                      {['Rank', 'Student', 'Email', 'Branch', 'Technology', 'Level'].map(h => (
                        <th key={h} style={{
                          padding: '12px 16px', textAlign: 'left',
                          fontSize: '0.65rem', fontWeight: 700, color: 'var(--primary-blue, #4F46E5)',
                          textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap',
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {topStudents.map((student, idx) => {
                      const rank = idx + 1;
                      const cfg  = rankConfig(rank);
                      const lvl  = levelColor(student.level);
                      const tc   = techColor(student.technology);
                      return (
                        <tr key={idx}
                          style={{ borderBottom: T.border, background: idx < 3 ? `${cfg.bg}` : '#FFFFFF', transition: 'background 0.1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                          onMouseLeave={e => e.currentTarget.style.background = idx < 3 ? cfg.bg : '#FFFFFF'}
                        >
                          {/* Rank */}
                          <td style={{ padding: '13px 16px', width: 60 }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '8px',
                              background: idx < 3 ? cfg.bg : '#F8FAFC',
                              border: `1px solid ${cfg.border}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: idx < 3 ? '1rem' : '0.8rem',
                              fontWeight: 800, color: cfg.color,
                            }}>
                              {idx < 3 ? cfg.label : `#${rank}`}
                            </div>
                          </td>
                          {/* Student */}
                          <td style={{ padding: '13px 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{
                                width: 34, height: 34, borderRadius: '50%',
                                background: `linear-gradient(135deg, ${cfg.strip}20, ${cfg.strip}40)`,
                                border: `1px solid ${cfg.border}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.75rem', fontWeight: 800, color: cfg.color, flexShrink: 0,
                              }}>
                                {student.name[0]}
                              </div>
                              <span style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.875rem' }}>{student.name}</span>
                            </div>
                          </td>
                          {/* Email */}
                          <td style={{ padding: '13px 16px', fontSize: '0.8125rem', color: '#64748B' }}>{student.email}</td>
                          {/* Branch */}
                          <td style={{ padding: '13px 16px', fontSize: '0.8125rem', color: '#374151' }}>{student.branch}</td>
                          {/* Tech */}
                          <td style={{ padding: '13px 16px' }}>
                            <span style={{ ...tc, padding: '3px 10px', borderRadius: T.radius.full, fontSize: '0.72rem', fontWeight: 700 }}>
                              {student.technology}
                            </span>
                          </td>
                          {/* Level */}
                          <td style={{ padding: '13px 16px' }}>
                            <span style={{ ...lvl, padding: '3px 12px', borderRadius: T.radius.full, fontSize: '0.8rem', fontWeight: 800 }}>
                              Level {student.level}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default CollegeTopPerformers;

